// ============================================
// calculateCheckoutValues.js (Utility for Payment Link with Dynamic Quantity + Low Inventory Discounts)
// NOTE: This is a utility function (not a hook), so it cannot use useQuery.
// The USD conversion rate and discount tiers should be fetched and passed by the caller.
// ============================================
import { calculateGatewayChargeFromMaster } from './chargeMasters';

export const calculateCheckoutValues = ({
  cartItems = [],
  selectedCurrency,
  appliedCoupon,
  selectedRate,
  selectedGatewayName = null,
  usdConversionRate,
  remoteFee = 0,
  couponsData,
  firstOrderDiscount = false,
  itemCodeGroups = {},
  discountTiers = [],
  manualCouponId,
  mailListDiscount = 0,
  // Rows from order.payment_gateway_charges (master). When not passed
  // (undefined/null) the legacy hard-coded rules are used.
  gatewayCharges = null,
  selectedGatewayId = null
}) => {
  const conversionRate = selectedCurrency?.conversion_rate || 1;
  const currencySymbol = selectedCurrency?.symbol || "₹";
  const isINR = selectedCurrency?.name?.toLowerCase() === "inr";
  const isUSD = selectedCurrency?.name?.toLowerCase() === "usd";

  const allCoupons = couponsData?.tenant_org_coupons || [];
  const manualCoupon = manualCouponId
    ? allCoupons.find(c => c.id === manualCouponId && c.auto_apply === false)
    : null;

  if (manualCoupon) {
    console.log("🎯 Manual Coupon Selected:", {
      id: manualCoupon.id,
      code: manualCoupon.code,
      value: manualCoupon.value,
      isPercentage: manualCoupon.is_percentage
    });
  }
  console.log("calculateCheckoutValues - Cart Items:", cartItems);
  console.log("Currency Info:", { isINR, isUSD, conversionRate, currencySymbol, usdConversionRate });
  console.log("All Available Coupons:", allCoupons);
  console.log("Item Code Groups:", itemCodeGroups);
  console.log("Discount Tiers:", discountTiers);
  console.log("Mail List Discount Rate:", mailListDiscount);

  // --------------------------
  // Get TOTAL discount (quantity + low inventory) for a specific item
  // --------------------------
  // ✅ FIX: low-inventory discount is now read directly off the line item
  // (item.lowInventoryDiscount), which the caller computes per-size using
  // ONLY that variant's own stock quantity. Previously this read
  // itemCodeGroups[itemCode].lowInventoryDiscount, which aggregated stock
  // across every variant sharing the same item_code — so two low-stock
  // variants of the same product could push the combined total above the
  // threshold and cancel each other's discount out entirely.
  //
  // The quantity-tier discount is unaffected by this fix and correctly
  // stays aggregated by item_code (it's genuinely about total quantity
  // ordered across variants, not per-variant stock).
  const getTotalItemDiscount = (item) => {
    const itemCode = item.item_code ?? item.itemCode;

    const quantityDiscount =
      (itemCode && itemCodeGroups[itemCode]?.quantityDiscount) || 0;

    const lowInventoryDiscount = item.lowInventoryDiscount || 0;

    // Stack discounts: (1 - quantityDiscount) * (1 - lowInventoryDiscount)
    const totalDiscount = 1 - ((1 - quantityDiscount) * (1 - lowInventoryDiscount));

    console.log(`💰 Total Discount for ${item.name}:`, {
      quantityDiscount: `${(quantityDiscount * 100).toFixed(0)}%`,
      lowInventoryDiscount: `${(lowInventoryDiscount * 100).toFixed(0)}%`,
      totalDiscount: `${(totalDiscount * 100).toFixed(1)}%`
    });

    return totalDiscount;
  };

  // --------------------------
  // Convert price using MULTIPLICATION
  // --------------------------
  const convertPrice = (basePrice, itemCurrencySymbol = "₹") => {
    if (!basePrice) return 0;

    const isItemInINR = itemCurrencySymbol === "₹";
    const isSelectedINR = currencySymbol === "₹";

    if ((isItemInINR && isSelectedINR) || (!isItemInINR && !isSelectedINR)) {
      return basePrice;
    }
    if (isItemInINR && !isSelectedINR) {
      return basePrice / conversionRate;
    }
    if (!isItemInINR && isSelectedINR) {
      return basePrice * conversionRate;
    }
    return basePrice;
  };

  // --------------------------
  // Calculate PLATING CHARGE separately for breakdown display
  // Discount is NOT applied here — it's applied in the subtotal loop
  // --------------------------
  const calculatePlatingCharge = () => {
    return cartItems.reduce((sum, item) => {
      const itemCurrencySymbol = item.currencySymbol || "₹";
      const weight = Number(item.weight) || 0;
      const platingPricePerGram = Number(item.plating_price_per_gram) || 0;
      const quantity = Number(item.quantity) || 1;

      if (platingPricePerGram > 0 && weight > 0) {
        const platingCost = weight * platingPricePerGram * quantity;
        return sum + convertPrice(platingCost, itemCurrencySymbol);
      }

      if (Array.isArray(item.wholesaleSelections) && item.wholesaleSelections.length) {
        const wholesalePlating = item.wholesaleSelections.reduce((wsSum, sel) => {
          const selWeight = Number(sel.weight) || Number(item.weight) || 0;
          const selPlatingPricePerGram = Number(sel.plating_price_per_gram) || 0;
          const selQty = Number(sel.qty) || 1;

          if (selPlatingPricePerGram > 0 && selWeight > 0) {
            const platingCost = selWeight * selPlatingPricePerGram * selQty;
            return wsSum + platingCost;
          }
          return wsSum;
        }, 0);
        return sum + convertPrice(wholesalePlating, itemCurrencySymbol);
      }

      return sum;
    }, 0);
  };

  // --------------------------
  // Calculate RAW price of each item WITH PLATING but WITHOUT discount
  // Discount is applied AFTER in the subtotal loop
  // --------------------------
  const calculateItemPrice = (item, quantity = 1) => {
    const itemCurrencySymbol = item.currencySymbol || "₹";

    if (item.weight && item.pricePerGram) {
      const weight = Number(item.weight) || 0;
      const pricePerGram = Number(item.pricePerGram) || 0;
      const platingPricePerGram = Number(item.plating_price_per_gram) || 0;
      const basePrice = weight * (pricePerGram + platingPricePerGram);
      return convertPrice(basePrice, itemCurrencySymbol) * quantity;
    }

    if (Array.isArray(item.wholesaleSelections) && item.wholesaleSelections.length) {
      const wholesaleTotal = item.wholesaleSelections.reduce((sum, sel) => {
        const selWeight = Number(sel.weight) || Number(item.weight) || 0;
        const selPricePerGram = Number(item.pricePerGram) || 0;
        const selPlatingPricePerGram = Number(sel.plating_price_per_gram) || 0;
        const selQty = Number(sel.qty) || 1;
        const basePrice = selWeight * (selPricePerGram + selPlatingPricePerGram) * selQty;
        return sum + basePrice;
      }, 0);
      return convertPrice(wholesaleTotal, itemCurrencySymbol) * quantity;
    }

    if (item.unitPrice !== undefined && item.unitPrice !== null) {
      return convertPrice(Number(item.unitPrice) || 0, itemCurrencySymbol) * quantity;
    }

    return convertPrice(Number(item.price) || 0, itemCurrencySymbol) * quantity;
  };

  // --------------------------
  // Subtotal — discount applied AFTER calculateItemPrice, not inside it
  // --------------------------
  // NOTE: when an item carries wholesaleSelections, each selection already
  // has its own qty baked into calculateItemPrice's wholesaleTotal reduce,
  // so we must NOT also multiply by item.quantity here (that would double
  // count quantity for wholesale line items).
const subtotal = cartItems.reduce((sum, item) => {
  const itemQuantity = (Array.isArray(item.wholesaleSelections) && item.wholesaleSelections.length)
    ? 1
    : (item.quantity || 1);
  const rawPrice = calculateItemPrice(item, itemQuantity);
  const discount = getTotalItemDiscount(item);

  // ✅ FIX: round each line item to cents BEFORE summing, so subtotal
  // matches the sum of the per-item totals actually shown in the UI.
  // Previously we summed full-precision floats and only rounded the
  // final subtotal once, which could differ from the displayed total
  // by a cent due to how individual items' fractional cents round.
  const itemTotal = Number((rawPrice * (1 - discount)).toFixed(2));

  console.log("📊 Item Calculation:", {
    name: item.name,
    itemCode: item.itemCode,
    size: item.size,
    plating: item.plating_name,
    quantity: itemQuantity,
    weight: item.weight,
    pricePerGram: item.pricePerGram,
    platingPricePerGram: item.plating_price_per_gram,
    rawPrice: rawPrice.toFixed(2),
    discount: `${(discount * 100).toFixed(1)}%`,
    itemTotal: itemTotal.toFixed(2)
  });

  return sum + itemTotal;
}, 0);

  const platingCharge = calculatePlatingCharge();

  console.log("💰 Subtotal (with plating, discounts applied after price):", subtotal.toFixed(2));
  console.log("✨ Plating Charge (informational):", platingCharge.toFixed(2));

  // --------------------------
  // Convert subtotal to USD for coupon eligibility check
  // --------------------------
  let subtotalInUSD;

  if (isUSD) {
    subtotalInUSD = subtotal;
  } else if (isINR) {
    subtotalInUSD = subtotal / usdConversionRate;
  } else {
    const subtotalInINR = subtotal * conversionRate;
    subtotalInUSD = subtotalInINR / usdConversionRate;

    console.log("💱 Subtotal Currency Conversion:", {
      original: subtotal.toFixed(2),
      currency: currencySymbol,
      conversionRate,
      subtotalInINR: subtotalInINR.toFixed(2),
      usdConversionRate,
      subtotalInUSD: subtotalInUSD.toFixed(2)
    });
  }

  // --------------------------
  // Find the best applicable auto coupon
  // --------------------------
  let bestApplicableCoupon = null;

  if (allCoupons && allCoupons.length > 0) {
    const eligibleCoupons = allCoupons.filter(coupon => {
      if (!coupon.auto_apply) return false;
      if (!coupon.value) return false;
      const minAmount = Number(coupon.min_amount_to_apply) || 0;
      const isEligible = subtotalInUSD >= minAmount;

      console.log(`🎫 Checking coupon "${coupon.name}":`, {
        code: coupon.code,
        minAmount,
        subtotalInUSD: subtotalInUSD.toFixed(2),
        isEligible
      });

      return isEligible;
    });

    if (eligibleCoupons.length > 0) {
      bestApplicableCoupon = eligibleCoupons.sort((a, b) => {
        const discountA = a.is_percentage ? (subtotal * a.value) / 100 : a.value;
        const discountB = b.is_percentage ? (subtotal * b.value) / 100 : b.value;
        return discountB - discountA;
      })[0];

      console.log("✅ Best applicable coupon:", {
        name: bestApplicableCoupon.name,
        code: bestApplicableCoupon.code,
        value: bestApplicableCoupon.value,
        isPercentage: bestApplicableCoupon.is_percentage
      });
    } else {
      console.log("❌ No eligible coupons for subtotal USD:", subtotalInUSD.toFixed(2));
    }
  } else {
    console.log("❌ No coupons available");
  }

  // --------------------------
  // Auto coupon discount
  // --------------------------
  let couponDiscount = 0;

  if (bestApplicableCoupon) {
    if (bestApplicableCoupon.is_percentage) {
      const discount = (subtotal * bestApplicableCoupon.value) / 100;
      couponDiscount = bestApplicableCoupon.max_discount_amount
        ? Math.min(discount, bestApplicableCoupon.max_discount_amount)
        : discount;
    } else {
      couponDiscount = bestApplicableCoupon.fixed_amount || 0;
    }

    console.log("🏷️ Auto Coupon Applied:", couponDiscount.toFixed(2));
  }

  // --------------------------
  // Manual coupon discount (stacked on top of auto coupon)
  // --------------------------
  let manualCouponDiscount = 0;

  if (manualCoupon) {
    const subtotalAfterAuto = subtotal - couponDiscount;

    if (manualCoupon.is_percentage) {
      const discount = (subtotalAfterAuto * (manualCoupon.value || 0)) / 100;
      manualCouponDiscount = manualCoupon.max_discount_amount
        ? Math.min(discount, manualCoupon.max_discount_amount)
        : discount;
    } else {
      const flatAmount = manualCoupon.fixed_amount ?? manualCoupon.value ?? 0;

      if (manualCoupon.currency_choosen?.name === 'USD') {
        manualCouponDiscount = isUSD
          ? flatAmount
          : isINR
            ? flatAmount * usdConversionRate
            : (flatAmount / conversionRate) * usdConversionRate;
      } else if (manualCoupon.currency_choosen?.name === 'INR') {
        manualCouponDiscount = isINR
          ? flatAmount
          : flatAmount / usdConversionRate;
      } else {
        manualCouponDiscount = flatAmount;
      }
    }

    console.log("🎟️ Manual Coupon Applied:", manualCouponDiscount.toFixed(2));
  }

  // --------------------------
  // First order discount (5% of subtotal)
  // --------------------------
  let firstOrderDiscountAmount = 0;

  if (firstOrderDiscount) {
    firstOrderDiscountAmount = subtotal * 0.05;
    console.log("🎉 First Order Discount (5%):", firstOrderDiscountAmount.toFixed(2));
  }

  // --------------------------
  // Mail list discount — multiplier (e.g. 0.05 = 5%) applied to the
  // subtotal AFTER auto coupon, manual coupon, and first-order discount
  // have already been deducted. Kept as a distinct promo from the above.
  // --------------------------
  let mailListDiscountAmount = 0;

  if (mailListDiscount && mailListDiscount > 0) {
    const subtotalAfterCoupons =
      subtotal - couponDiscount - manualCouponDiscount - firstOrderDiscountAmount;
    mailListDiscountAmount = subtotalAfterCoupons * mailListDiscount;

    console.log("📧 Mail List Discount Applied:", mailListDiscountAmount.toFixed(2));
  }

  // --------------------------
  // Shipping Estimate
  // --------------------------
  let shippingEstimate = 0;

  if (selectedRate) {
    const rateAmount = selectedRate.shipping_cost ?? selectedRate.total_charge ?? 0;
    const rateCurrency = selectedRate.currency?.toUpperCase() || (isINR ? "INR" : "USD");
    const rateIsINR = rateCurrency === "INR";
    const selectedIsINR = currencySymbol === "₹";

    if ((rateIsINR && selectedIsINR) || (!rateIsINR && !selectedIsINR)) {
      shippingEstimate = rateAmount;
    } else if (rateIsINR && !selectedIsINR) {
      shippingEstimate = rateAmount / conversionRate;
    } else if (!rateIsINR && selectedIsINR) {
      shippingEstimate = rateAmount * conversionRate;
    } else {
      shippingEstimate = rateAmount;
    }
  }

  // --------------------------
  // Insurance (3% of discounted subtotal + shipping + remoteFee)
  // Now also nets out mailListDiscountAmount, same as the other discounts
  // --------------------------
  const subtotalAfterDiscount =
    subtotal - couponDiscount - manualCouponDiscount - firstOrderDiscountAmount - mailListDiscountAmount;
  const insuranceCharge = 0.03 * (subtotalAfterDiscount + shippingEstimate + remoteFee);

  // --------------------------
  // Base order total (before gateway charges)
  // --------------------------
  const baseOrderTotal =
    subtotal
    + shippingEstimate
    + insuranceCharge
    + remoteFee
    - couponDiscount
    - manualCouponDiscount
    - firstOrderDiscountAmount
    - mailListDiscountAmount;

  // --------------------------
  // Payment Gateway Charges — from the gateway charge master
  // (order.payment_gateway_charges), legacy rules if master not passed
  // --------------------------
  let paymentGatewayCharge = 0;

  if (selectedGatewayName || selectedGatewayId) {
    paymentGatewayCharge = calculateGatewayChargeFromMaster({
      rules: gatewayCharges,
      gatewayId: selectedGatewayId,
      gatewayName: selectedGatewayName,
      baseOrderTotal,
      isINR,
      isUSD,
      conversionRate,
      usdConversionRate,
    });

    console.log("🏦 Gateway Charge:", {
      gateway: selectedGatewayName,
      source: Array.isArray(gatewayCharges) ? 'master' : 'legacy',
      baseOrderTotal: baseOrderTotal.toFixed(2),
      charge: paymentGatewayCharge.toFixed(2),
      currencySymbol
    });
  }

  // --------------------------
  // Final Total
  // --------------------------
  const orderTotal = baseOrderTotal + paymentGatewayCharge;

  console.log("🧾 Final Checkout Summary:", {
    subtotal: subtotal.toFixed(2),
    subtotalInUSD: subtotalInUSD.toFixed(2),
    bestCoupon: bestApplicableCoupon?.code || appliedCoupon?.code || 'None',
    manualCoupon: manualCoupon?.code || 'None',
    platingCharge: platingCharge.toFixed(2),
    shippingEstimate: shippingEstimate.toFixed(2),
    insuranceCharge: insuranceCharge.toFixed(2),
    remoteFee: remoteFee.toFixed(2),
    couponDiscount: couponDiscount.toFixed(2),
    manualCouponDiscount: manualCouponDiscount.toFixed(2),
    firstOrderDiscountAmount: firstOrderDiscountAmount.toFixed(2),
    mailListDiscountAmount: mailListDiscountAmount.toFixed(2),
    baseOrderTotal: baseOrderTotal.toFixed(2),
    paymentGatewayCharge: paymentGatewayCharge.toFixed(2),
    orderTotal: orderTotal.toFixed(2),
    currencySymbol,
    selectedGateway: selectedGatewayName,
    isINR
  });

  // --------------------------
  // Return summary
  // --------------------------
  return {
    subtotal,
    subtotalInUSD,
    shippingEstimate,
    insuranceCharge,
    couponDiscount,
    manualCouponDiscount,
    manualCoupon,
    mailListDiscountAmount,
    mailListDiscount,
    paymentGatewayCharge,
    platingCharge,
    orderTotal,
    currencySymbol,
    isINR,
    remoteFee,
    firstOrderDiscountAmount,
    bestApplicableCoupon,
    allCoupons,
    usdConversionRate,
    calculateItemPrice,
  };
};